import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, Shield, TrendingUp, PieChart, AlertTriangle, CheckCircle } from 'lucide-react';

interface AMFRiskProfilerProps {
  onComplete: (riskData: any) => void;
  onNavigateBack: () => void;
}

interface UserProfile {
  horizon: string;
  capacity: string;
  riskTolerance: string;
  experience: string;
  patrimonyRatio: string;
  lossReaction: string;
  income: string;
  debts: string;
}

interface ChatMessage {
  type: string;
  content: string;
  timestamp: Date;
  explanation?: string;
  contextual?: boolean;
}

interface UserAnswer {
  question: string;
  answer: string;
  type: string;
  timestamp: Date;
}

const AMFRiskProfiler: React.FC<AMFRiskProfilerProps> = ({ onComplete, onNavigateBack }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    horizon: '',
    capacity: '',
    riskTolerance: '',
    experience: '',
    patrimonyRatio: '',
    lossReaction: '',
    income: '',
    debts: ''
  });
  const [showResults, setShowResults] = useState<boolean>(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [messageQueue, setMessageQueue] = useState<any[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState<boolean>(false);
  const [botPersonality] = useState({
    name: 'AMF Assistant',
    emoji: '🤖'
  });

  // Matrice de scoring AMF détaillée
  const scoringMatrix = {
    horizon: {
      "Moins de 3 ans": 1,
      "3 à 8 ans": 2,
      "8 à 15 ans": 3,
      "Plus de 15 ans": 4
    },
    capacity: {
      "Moins de 100€": 1,
      "100 à 200€": 2,
      "200 à 400€": 3,
      "400 à 600€": 4,
      "Plus de 600€": 5
    },
    riskReaction: {
      "Je paniquerais et vendrais tout": 1,
      "Je serais inquiet mais patienterais": 2,
      "Je resterais serein": 3,
      "J'en profiterais pour investir plus": 4
    },
    experience: {
      "🔰 Débutant - Epargne bancaire uniquement": 1,
      "🏦 Basique - Assurance-vie garantie": 2,
      "📊 Intermédiaire - Actions, immobilier, OPCVM": 3,
      "💼 Expérimenté - Investi sur les marchés financiers depuis +5 ans": 4
    },
    patrimonyShare: {
      "⚠️ Plus de 50% de mes économies": 1,
      "🔶 Entre 35% et 50%": 2,
      "🔸 Entre 20% et 35%": 3,
      "✅ Moins de 20% - J'ai d'autres placements": 4
    },
    lossReaction: {
      "J'arrêterais immédiatement": 1,
      "Je diminuerais mes versements": 2,
      "Je continuerais normalement": 3,
      "J'augmenterais mes versements (opportunité)": 4
    },
    income: {
      "Moins de 2000€": 1,
      "2000€ à 3500€": 2,
      "3500€ à 5000€": 3,
      "5000€ à 8000€": 4,
      "Plus de 8000€": 5
    },
    debts: {
      "Oui, des crédits consommation (>5% taux)": 1,
      "Crédit immobilier uniquement": 3,
      "Quelques dettes mineures": 4,
      "Aucune dette": 5
    }
  };

  // Allocations optimisées selon profil AMF
  const allocationProfiles = {
    conservateur: { scpi: 50, gfi: 30, sukukFunds: 15, sukukProjects: 5 },
    modere: { scpi: 35, gfi: 20, sukukFunds: 25, sukukProjects: 20 },
    dynamique: { scpi: 25, gfi: 10, sukukFunds: 30, sukukProjects: 35 },
    offensif: { scpi: 15, gfi: 5, sukukFunds: 35, sukukProjects: 45 }
  };

  const questions = useMemo(() => [
    {
      text: "Dans combien de temps aurez-vous besoin de cet argent ?",
      type: "horizon",
      explanation: "Votre horizon d'investissement détermine le niveau de risque acceptable.",
      options: ["Moins de 3 ans", "3 à 8 ans", "8 à 15 ans", "Plus de 15 ans"]
    },
    {
      text: "Combien pouvez-vous épargner chaque mois sans impacter votre quotidien ?",
      type: "capacity",
      explanation: "Il est important de ne jamais investir plus que ce que vous pouvez vous permettre.",
      options: ["Moins de 100€", "100 à 200€", "200 à 400€", "400 à 600€", "Plus de 600€"]
    },
    {
      text: "Si votre portefeuille perdait 10% en quelques mois, que feriez-vous ?",
      type: "riskReaction",
      explanation: "Cette question évalue votre tolérance psychologique au risque.",
      options: [
        "Je paniquerais et vendrais tout",
        "Je serais inquiet mais patienterais",
        "Je resterais serein, c'est normal",
        "J'en profiterais pour investir davantage"
      ]
    },
    {
      text: "Quelle est votre expérience en investissement halal ou traditionnel ?",
      type: "experience",
      explanation: "Votre expérience influence les produits qui vous sont adaptés.",
      options: [
        "🔰 Débutant - Livret A, épargne bancaire uniquement",
        "🏦 Basique - Assurance-vie en fonds euros",
        "📊 Intermédiaire - Actions, SCPI, OPCVM",
        "💼 Expérimenté - Marchés financiers depuis +5 ans"
      ]
    },
    {
      text: "Cette épargne représente quelle part de votre patrimoine total ?",
      type: "patrimonyShare",
      explanation: "Règle d'or : ne jamais mettre tous ses œufs dans le même panier.",
      options: [
        "⚠️ Plus de 50% de mes économies",
        "🔶 Entre 35% et 50%",
        "🔸 Entre 20% et 35%",
        "✅ Moins de 20% - J'ai d'autres placements sécurisés"
      ]
    },
    {
      text: "En cas de perte de 20% sur une année, quelle serait votre réaction ?",
      type: "lossReaction",
      explanation: "Question de contrôle pour valider votre profil de risque.",
      options: [
        "J'arrêterais immédiatement tous mes investissements",
        "Je diminuerais fortement mes versements",
        "Je continuerais mes versements normalement",
        "J'augmenterais mes versements (c'est une opportunité)"
      ]
    },
    {
      text: "Quel est votre revenu mensuel net approximatif ?",
      type: "income",
      explanation: "Pour vérifier l'adéquation entre vos revenus et votre capacité d'épargne.",
      options: ["Moins de 2000€", "2000€ à 3500€", "3500€ à 5000€", "5000€ à 8000€", "Plus de 8000€"]
    },
    {
      text: "Avez-vous des dettes importantes (crédit immobilier, consommation) ?",
      type: "debts",
      explanation: "Il est essentiel de rembourser les dettes avant d'investir.",
      options: [
        "Oui, des crédits consommation (>5% taux)",
        "Crédit immobilier uniquement",
        "Quelques dettes mineures",
        "Aucune dette"
      ]
    }
  ], []);

  const calculateAMFProfile = () => {
    const scores = {
      horizon: (scoringMatrix.horizon as any)[userProfile.horizon] || 1,
      capacity: (scoringMatrix.capacity as any)[userProfile.capacity] || 1,
      risk: (scoringMatrix.riskReaction as any)[userProfile.riskTolerance] || 1,
      experience: (scoringMatrix.experience as any)[userProfile.experience] || 1,
      patrimony: (scoringMatrix.patrimonyShare as any)[userProfile.patrimonyRatio] || 1,
      loss: (scoringMatrix.lossReaction as any)[userProfile.lossReaction] || 1,
      income: (scoringMatrix.income as any)[userProfile.income] || 1,
      debts: (scoringMatrix.debts as any)[userProfile.debts] || 1
    };

    // Pondération selon importance AMF
    const weightedScore = (
      scores.horizon * 0.25 +      // 25% - Horizon crucial
      scores.risk * 0.20 +         // 20% - Tolérance au risque
      scores.experience * 0.20 +   // 20% - Expérience
      scores.patrimony * 0.15 +    // 15% - Part du patrimoine
      scores.capacity * 0.10 +     // 10% - Capacité financière
      scores.loss * 0.10           // 10% - Contrôle cohérence
    );

    // Détermination du profil AMF
    let profile;
    if (weightedScore <= 1.5) profile = 'conservateur';
    else if (weightedScore <= 2.5) profile = 'modere';
    else if (weightedScore <= 3.2) profile = 'dynamique';
    else profile = 'offensif';

    // Vérifications de cohérence AMF
    const warnings = [];
    
    if (scores.horizon <= 2 && scores.risk >= 3) {
      warnings.push("⚠️ Attention : Horizon court mais tolérance au risque élevée");
      profile = 'conservateur';
    }
    
    if (scores.patrimony <= 2 && profile === 'offensif') {
      warnings.push("⚠️ Attention : Part importante du patrimoine investie");
      profile = 'modere';
    }
    
    if (scores.experience <= 2 && profile === 'offensif') {
      warnings.push("⚠️ Attention : Expérience limitée pour un profil offensif");
      profile = 'dynamique';
    }

    if (scores.debts <= 2) {
      warnings.push("⚠️ Attention : Dettes importantes détectées - remboursement prioritaire recommandé");
      profile = 'conservateur';
    }

    return { profile, scores, warnings, weightedScore };
  };

  const generatePersonalizedAllocation = () => {
    const analysis = calculateAMFProfile();
    const baseAllocation = (allocationProfiles as any)[analysis.profile];
    
    let allocation = { ...baseAllocation };
    
    // Ajustements fins selon l'horizon
    if (analysis.scores.horizon >= 4) {
      allocation.sukukProjects += 5;
      allocation.gfi -= 5;
    }

    return { allocation, analysis };
  };


  const addContextualBotResponse = (answer: any, questionType: any) => {
    let response = "";

    switch(questionType) {
      case 'horizon':
        if (answer.includes('Moins de 3 ans')) {
          response = "Je vois, un horizon court. Je privilégierai la sécurité dans mes recommandations.";
        } else if (answer.includes('Plus de 15 ans')) {
          response = "🚀 Excellent ! Un horizon long permet d'optimiser la croissance.";
        } else {
          response = "📅 Parfait, c'est un bon horizon d'investissement.";
        }
        break;
      case 'capacity':
        if (answer.includes('Plus de 600€')) {
          response = "💰 Excellente capacité d'épargne !";
        } else if (answer.includes('Moins de 100€')) {
          response = "🌱 C'est un bon début !";
        } else {
          response = "👍 Capacité d'épargne raisonnable.";
        }
        break;
      case 'riskReaction':
        if (answer.includes('paniquerais')) {
          response = "🛡️ Je comprends, je recommanderai des investissements très stables.";
        } else if (answer.includes('profiterais')) {
          response = "💪 Profil investisseur confirmé !";
        } else {
          response = "⚖️ Approche équilibrée, très sage.";
        }
        break;
      case 'experience':
        if (answer.includes('Débutant')) {
          response = "🌱 Parfait pour commencer !";
        } else if (answer.includes('Expérimenté')) {
          response = "💼 Excellent, votre expérience sera un atout !";
        } else {
          response = "📈 Bonne expérience.";
        }
        break;
      case 'patrimonyShare':
        if (answer.includes('Plus de 50%')) {
          response = "⚠️ Important de diversifier, approche prudente recommandée.";
        } else if (answer.includes('Moins de 20%')) {
          response = "✅ Excellente diversification !";
        } else {
          response = "🔸 Répartition raisonnable.";
        }
        break;
      case 'lossReaction':
        if (answer.includes('arrêterais')) {
          response = "🛑 Compris, investissements très stables pour vous.";
        } else if (answer.includes('augmenterais')) {
          response = "🎯 Vision d'investisseur aguerri !";
        } else {
          response = "💪 Approche mesurée.";
        }
        break;
      case 'income':
        response = "💰 Merci pour cette information.";
        break;
      case 'debts':
        if (answer.includes('Aucune dette')) {
          response = "✅ Parfait ! Situation idéale pour investir.";
        } else if (answer.includes('crédits consommation')) {
          response = "⚠️ Important : remboursement prioritaire recommandé.";
        } else {
          response = "🏠 Situation financière notée.";
        }
        break;
      default:
        response = "👍 Merci pour votre réponse.";
        break;
    }

    if (response) {
      setMessageQueue([
        { type: 'delay', duration: 1500 },
        {
          type: 'bot',
          content: response,
          contextual: true,
          timestamp: new Date()
        }
      ]);
    }
  };

  const selectOption = (answer: any) => {
    const currentQuestion = questions[currentQuestionIndex];
    
    // Masquer les options
    setShowOptions(false);
    
    // Stockage de la réponse
    const newAnswer = {
      question: currentQuestion.text,
      answer: answer,
      type: currentQuestion.type,
      timestamp: new Date()
    };
    
    setUserAnswers(prev => [...prev, newAnswer]);
    setChatMessages(prev => [...prev, { type: 'user', content: answer, timestamp: new Date() }]);

    // Mise à jour du profil utilisateur
    setUserProfile(prev => ({
      ...prev,
      [currentQuestion.type]: answer
    }));

    // Réponse contextuelle du bot
    addContextualBotResponse(answer, currentQuestion.type);

    // Passage à la question suivante ou fin
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      askNextQuestionByIndex(nextIndex);
    } else {
      // Fin du questionnaire
      setMessageQueue([
        { type: 'delay', duration: 2000 },
        {
          type: 'bot',
          content: "🎉 Parfait ! J'ai toutes les informations nécessaires.",
          timestamp: new Date()
        },
        { type: 'delay', duration: 2000 },
        {
          type: 'bot',
          content: "📊 Je vais maintenant analyser votre profil AMF...",
          timestamp: new Date()
        },
        { type: 'delay', duration: 2000 },
        { type: 'showResults' }
      ]);
    }
  };
  
  // Gestion de l'affichage des résultats
  useEffect(() => {
    if (messageQueue.length === 1 && messageQueue[0].type === 'showResults') {
      setMessageQueue([]);
      setShowResults(true);
    }
  }, [messageQueue]);

  const askNextQuestionByIndex = (questionIndex: any) => {
    const nextQuestion = questions[questionIndex];
    
    setMessageQueue([
      { type: 'delay', duration: 2500 },
      {
        type: 'question',
        content: nextQuestion.text,
        explanation: nextQuestion.explanation,
        timestamp: new Date()
      },
      { type: 'delay', duration: 1500 },
      { type: 'showOptions' }
    ]);
  };

  const resetQuestionnaire = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setUserProfile({
      horizon: '',
      capacity: '',
      riskTolerance: '',
      experience: '',
      patrimonyRatio: '',
      lossReaction: '',
      income: '',
      debts: ''
    });
    setShowResults(false);
    setChatMessages([]);
    setMessageQueue([]);
    setIsProcessingQueue(false);
    setShowOptions(false);
    setIsTyping(false);
  };

  const handleComplete = () => {
    const result = generatePersonalizedAllocation();
    if (onComplete) {
      onComplete({
        riskProfile: result.analysis.profile,
        riskScore: Math.round(result.analysis.weightedScore * 10) / 10,
        allocation: result.allocation,
        userAnswers: userAnswers,
        analysis: result.analysis
      });
    }
  };

  const getProfileColor = (profile: any) => {
    const colors = {
      conservateur: 'text-blue-600 bg-blue-50',
      modere: 'text-green-600 bg-green-50',
      dynamique: 'text-orange-600 bg-orange-50',
      offensif: 'text-red-600 bg-red-50'
    };
    return (colors as any)[profile] || 'text-gray-600 bg-gray-50';
  };

  const getProfileIcon = (profile: any) => {
    const icons = {
      conservateur: Shield,
      modere: TrendingUp,
      dynamique: PieChart,
      offensif: TrendingUp
    };
    const IconComponent = (icons as any)[profile] || Shield;
    return <IconComponent className="w-5 h-5" />;
  };

  // Processeur de queue de messages
  useEffect(() => {
    if (messageQueue.length > 0 && !isProcessingQueue) {
      setIsProcessingQueue(true);
      
      const processNextMessage = async () => {
        const [nextMessage, ...remainingMessages] = messageQueue;
        
        if (nextMessage.type === 'delay') {
          await new Promise(resolve => setTimeout(resolve, nextMessage.duration));
        } else {
          setChatMessages(prev => [...prev, nextMessage]);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        setMessageQueue(remainingMessages);
        setIsProcessingQueue(false);
      };
      
      processNextMessage();
    }
  }, [messageQueue, isProcessingQueue]);
  
  // Initialisation unique
  useEffect(() => {
    if (chatMessages.length === 0 && messageQueue.length === 0) {
      setMessageQueue([
        {
          type: 'bot',
          content: '👋 Bonjour ! Je suis votre assistant AMF pour l\'investissement halal.',
          timestamp: new Date()
        },
        { type: 'delay', duration: 2000 },
        {
          type: 'question',
          content: questions[0].text,
          explanation: questions[0].explanation,
          timestamp: new Date()
        },
        { type: 'delay', duration: 1500 },
        { type: 'showOptions' }
      ]);
    }
  }, [chatMessages.length, messageQueue.length, questions]);
  
  // Gestion de l'affichage des options
  useEffect(() => {
    if (messageQueue.length === 1 && messageQueue[0].type === 'showOptions') {
      setMessageQueue([]);
      setShowOptions(true);
    }
  }, [messageQueue]);

  if (showResults) {
    const result = generatePersonalizedAllocation();
    
    return (
      <div className="amf-results-container">
        {/* Header moderne */}
        <div className="amf-results-header">
          <div className="amf-header-content">
            <h1 className="amf-header-title">Analyse AMF Complète</h1>
            <p className="amf-header-subtitle">Votre profil d'investissement personnalisé</p>
          </div>
        </div>

        <div className="amf-results-grid">
          {/* Profil de Risque - Card moderne */}
          <div className="amf-profile-card">
            <div className="amf-profile-badge">
              <div className={`amf-profile-indicator ${result.analysis.profile}`}>
                {getProfileIcon(result.analysis.profile)}
              </div>
              <div className="amf-profile-info">
                <span className="amf-profile-label">Profil Investisseur</span>
                <span className="amf-profile-type">{result.analysis.profile.toUpperCase()}</span>
              </div>
            </div>
            
            <div className="amf-score-section">
              <div className="amf-score-header">
                <span className="amf-score-label">Score de risque AMF</span>
                <span className="amf-score-value">{result.analysis.weightedScore.toFixed(1)}/4</span>
              </div>
              <div className="amf-progress-container">
                <div className="amf-progress-bar">
                  <div 
                    className="amf-progress-fill"
                    style={{ width: `${(result.analysis.weightedScore / 4) * 100}%` }}
                  />
                </div>
              </div>

              {/* Détail des Scores - Design moderne */}
        <div className="amf-analysis-card">
          <div className="amf-card-header">
            <div className="amf-card-icon">📊</div>
            <h3 className="amf-card-title">Détail de l'Analyse</h3>
          </div>
          <div className="amf-scores-grid">
            {Object.entries(result.analysis.scores).map(([key, score]) => (
              <div key={key} className="amf-score-item">
                <div className="amf-score-circle">
                <span className="amf-score-label">{key}</span>
                <span> </span>
                <span className="amf-score-number">{score}</span>
                  
                </div>
              </div>
            ))}
          </div>
        </div>
            </div>

            {result.analysis.warnings.length > 0 && (
              <div className="amf-warnings">
                <div className="amf-warning-header">
                  <AlertTriangle className="amf-warning-icon" />
                  <span>Points d'attention</span>
                </div>
                <div className="amf-warning-list">
                  {result.analysis.warnings.map((warning, index) => (
                    <p key={index} className="amf-warning-item">{warning}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
          

          {/* Allocation Recommandée - Design moderne */}
          <div className="amf-allocation-card">
            <div className="amf-card-header">
              <PieChart className="amf-card-icon" />
              <h3 className="amf-card-title">Allocation Recommandée</h3>
            </div>
            
            <div className="amf-allocation-grid">
              <div className="amf-allocation-item scpi">
                <div className="amf-allocation-icon">🏢</div>
                <div className="amf-allocation-info">
                  <span className="amf-allocation-name">SCPI</span>
                  <span className="amf-allocation-desc">Immobilier locatif</span>
                </div>
                <div className="amf-allocation-percentage">{result.allocation.scpi}%</div>
              </div>
              
              <div className="amf-allocation-item gfi">
                <div className="amf-allocation-icon">📊</div>
                <div className="amf-allocation-info">
                  <span className="amf-allocation-name">GFI</span>
                  <span className="amf-allocation-desc">Stabilité et revenus</span>
                </div>
                <div className="amf-allocation-percentage">{result.allocation.gfi}%</div>
              </div>
              
              <div className="amf-allocation-item sukuk-funds">
                <div className="amf-allocation-icon">☪️</div>
                <div className="amf-allocation-info">
                  <span className="amf-allocation-name">Fonds Sukuk</span>
                  <span className="amf-allocation-desc">Revenus réguliers</span>
                </div>
                <div className="amf-allocation-percentage">{result.allocation.sukukFunds}%</div>
              </div>
              
              <div className="amf-allocation-item sukuk-projects">
                <div className="amf-allocation-icon">🚀</div>
                <div className="amf-allocation-info">
                  <span className="amf-allocation-name">Projets Sukuk</span>
                  <span className="amf-allocation-desc">Innovation et croissance</span>
                </div>
                <div className="amf-allocation-percentage">{result.allocation.sukukProjects}%</div>
              </div>
            </div>

            <div className="amf-compliance-badge">
              <CheckCircle className="amf-compliance-icon" />
              <span>Allocation conforme aux exigences AMF et principes éthiques</span>
            </div>
          </div>
        </div>

        

        <div className="amf-actions">
          <button
            onClick={resetQuestionnaire}
            className="amf-secondary-button"
          >
            Recommencer l'analyse
          </button>
          <button
            onClick={handleComplete}
            className="amf-primary-button"
          >
            Continuer vers le Bilan Patrimonial →
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="soeasi-container">
      <div className="soeasi-card">
        {/* En-tête */}
        <div className="soeasi-header">
          <h1 className="soeasi-title">Profilage AMF</h1>
          <p className="soeasi-subtitle">Évaluation du profil de risque - Investissement Halal</p>
        </div>

        {/* Progress */}
        <div className="soeasi-progress">
          <div className="soeasi-progress-bar">
            <div 
              className="soeasi-progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
          <span className="soeasi-progress-text">Question {currentQuestionIndex + 1} sur {questions.length}</span>
        </div>

        {/* Zone de Chat */}
        <div className="amf-chat-container">
          <div className="space-y-6 max-h-96 overflow-y-auto" id="chat-container">
            {chatMessages.map((message, index) => (
              <div key={index} className="animate-fade-in">
                {message.type === 'question' ? (
                  <div className="amf-clean-question">
                    <div className="text-lg font-medium text-gray-900 leading-relaxed">
                      {message.content}
                    </div>
                  </div>
                ) : message.type === 'bot' ? (
                  <div className="amf-bot-message">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{botPersonality.emoji}</span>
                      <span className="text-base font-medium text-gray-900">
                        {message.content}
                      </span>
                    </div>
                  </div>
                ) : message.content && message.content.trim() ? (
                  <div className="amf-user-response">
                    {message.content}
                  </div>
                ) : null}
              </div>
            ))}
            
            {/* Indicateur de frappe */}
            {isTyping && (
              <div className="flex justify-start animate-fade-in">
                <div className="amf-chat-bot max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{botPersonality.emoji} {botPersonality.name}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Options de Réponse */}
        {showOptions && (
          <div className="amf-options-section animate-fade-in">
            <div className="space-y-3">
              {questions[currentQuestionIndex].options.map((option, index) => (
                <div
                  key={index}
                  onClick={() => selectOption(option)}
                  className="amf-clean-option"
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="soeasi-navigation">
          <button
            onClick={onNavigateBack}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            ← Retour SOeasi
          </button>
        </div>

        {/* Pied de page */}
        <div className="text-center text-sm text-gray-500 mt-6">
          <p>🔒 Conformité AMF • ☪️ Investissement Halal • 📊 Scoring Personnalisé</p>
        </div>
      </div>
    </div>
  );
};

export default AMFRiskProfiler;
